import { requirePMSAuthLoader } from '@/routes/loaders/auth-loaders';
import { createPathRedirectLoader } from '@/routes/loaders/legacy-redirects';
import { lazy } from 'react';
import { replace, RouteObject } from 'react-router';

const MenuLayout = lazy(() => import('@/routes/layouts/MenuLayout'));

const MangroverManagePage = lazy(
  () => import('@/routes/pages/mangrover/manage/MangroverManagePage'),
);
const MangroverCreditsPage = lazy(
  () => import('@/routes/pages/mangrover/credits/MangroverCreditsPage'),
);

const CommunityCategoryPage = lazy(
  () => import('@/routes/pages/community/category/CommunityCategoryPage'),
);
const CommunityPostPage = lazy(() => import('@/routes/pages/community/post/CommunityPostPage'));
const CommunityMscPage = lazy(() => import('@/routes/pages/community/msc/CommunityMscPage'));

const SaleSlotPage = lazy(() => import('@/routes/pages/sale/slot/SaleSlotPage'));
const SaleContractPage = lazy(() => import('@/routes/pages/sale/contract/SaleContractPage'));
const SaleVacancyPage = lazy(() => import('@/routes/pages/sale/vacancy/vacancy-page'));
const SalePricePage = lazy(() => import('@/routes/pages/sale/price/SalePricePage'));
const SaleBranchPage = lazy(() => import('@/routes/pages/sale/branch/SaleBranchPage'));
const SaleRenewalPage = lazy(() => import('@/routes/pages/sale/renewal/SaleRenewalPage'));
const SaleTourReservationPage = lazy(
  () => import('@/routes/pages/sale/tour-reservation/SaleTourReservationPage'),
);
const SaleTourSchedulePage = lazy(
  () => import('@/routes/pages/sale/tour-schedule/SaleTourSchedulePage'),
);

const StayManagePage = lazy(() => import('@/routes/pages/stay/manage/StayManagePage'));
const StayConsentHistoryPage = lazy(
  () => import('@/routes/pages/stay/consent-history/StayConsentHistoryPage'),
);
const StayReservationPage = lazy(
  () => import('@/routes/pages/stay/reservation/stay-reservation-page'),
);

const BranchRegionManagePage = lazy(
  () => import('@/routes/pages/branch-region/manage/branch-region-manage-page'),
);
const BranchRegionMappingPage = lazy(
  () => import('@/routes/pages/branch-region/mapping/branch-region-mapping-page'),
);
const BranchRegionLifestyleTagPage = lazy(
  () => import('@/routes/pages/branch-region/lifestyle-tag/branch-region-lifestyle-tag-page'),
);
const BranchRegionCardAttributePage = lazy(
  () => import('@/routes/pages/branch-region/card-attribute/branch-region-card-attribute-page'),
);
const BranchRegionSalesInfoPage = lazy(
  () => import('@/routes/pages/branch-region/sales-info/branch-region-sales-info-page'),
);

const RoomStatusPage = lazy(() => import('@/routes/pages/room/status/RoomStatusPage'));
const RoomCardkeyStayPage = lazy(
  () => import('@/routes/pages/room/cardkey-stay/RoomCardkeyStayPage'),
);
const RoomDoorLockManagePage = lazy(
  () => import('@/routes/pages/room/door-lock-manage/RoomDoorLockManagePage'),
);

const FacilitySpacePage = lazy(() => import('@/routes/pages/facility/space/FacilitySpacePage'));
const FacilityInquiryPage = lazy(
  () => import('@/routes/pages/facility/inquiry/FacilityInquiryPage'),
);
const FacilitySchedulePage = lazy(
  () => import('@/routes/pages/facility/schedule/FacilitySchedulePage'),
);
const FacilitySpaceReservationsPage = lazy(
  () => import('@/routes/pages/facility/spaces-reservations/FacilitySpaceReservationsPage'),
);
const FacilitySpaceCalendarPage = lazy(
  () => import('@/routes/pages/facility/space-calendar/FacilitySpaceCalendarPage'),
);

const VisitorManagePage = lazy(() => import('@/routes/pages/visitor/manage/VisitorManagePage'));

const UserGroupManagePage = lazy(
  () => import('@/routes/pages/user-group/manage/UserGroupManagePage'),
);

const AdditionManagePage = lazy(() => import('@/routes/pages/addition/manage/AdditionManagePage'));

const NoticeManagePage = lazy(() => import('@/routes/pages/notice/manage/notice-manage-page'));

const ManualAuthorizerPage = lazy(
  () => import('@/routes/pages/setting/manual-authorizer/ManualAuthorizerPage'),
);
const BranchesPage = lazy(() => import('@/routes/pages/setting/branches/BranchesPage'));
const TermsManagePage = lazy(() => import('@/routes/pages/setting/terms/TermsManagePage'));
const TermsEditorPage = lazy(() => import('@/routes/pages/setting/terms/editor/TermsEditorPage'));
const ShortUrlManagePage = lazy(() => import('@/routes/pages/setting/short-url/short-url-page'));
const NotificationBranchSettingsPage = lazy(
  () =>
    import('@/routes/pages/setting/notification-branch-settings/notification-branch-settings-page'),
);

const PopupManagePage = lazy(() => import('@/routes/pages/popup/manage/PopupManagePage'));

const BranchInitPage = lazy(() => import('@/routes/pages/branch-init/branch-init-page'));

const BillingRegularPage = lazy(
  () => import('@/routes/pages/billing/regular/billing-regular-page'),
);
const BillingMoveOutPage = lazy(
  () => import('@/routes/pages/billing/move-out/billing-move-out-page'),
);
const BillingGeneralManagementFeePage = lazy(
  () => import('@/routes/pages/billing/general-management-fee/general-management-fee-page'),
);

const DailyPricingMetricsPage = lazy(
  () => import('@/routes/pages/daily-pricing/metrics/daily-pricing-metrics-page'),
);
const DailyPricingSummaryPage = lazy(
  () => import('@/routes/pages/daily-pricing/summary/daily-pricing-summary-page'),
);
const DailyPricingSettingsPage = lazy(
  () => import('@/routes/pages/daily-pricing/settings/daily-pricing-settings-page'),
);

export const protectedRoutes: RouteObject[] = [
  {
    loader: requirePMSAuthLoader,
    children: [
      {
        element: <MenuLayout />,
        children: [
          { index: true, loader: () => replace('/mangrover/manage') },

          // 이용자 메뉴
          { path: 'mangrover/manage', element: <MangroverManagePage /> },
          { path: 'mangrover/credits', element: <MangroverCreditsPage /> },

          // 커뮤니티 메뉴
          { path: 'community/post', element: <CommunityPostPage /> },
          { path: 'community/category', element: <CommunityCategoryPage /> },
          { path: 'community/msc', element: <CommunityMscPage /> },

          // LIVE 판매 메뉴
          { path: 'sale/slot', element: <SaleSlotPage /> },
          { path: 'sale/contract', element: <SaleContractPage /> },
          { path: 'sale/vacancy', element: <SaleVacancyPage /> },
          { path: 'sale/price', element: <SalePricePage /> },
          { path: 'sale/branch', element: <SaleBranchPage /> },
          { path: 'sale/renewal', element: <SaleRenewalPage /> },
          { path: 'sale/tour-reservation', element: <SaleTourReservationPage /> },
          { path: 'sale/tour-schedule', element: <SaleTourSchedulePage /> },

          // STAY 판매 메뉴
          { path: 'stay/manage', element: <StayManagePage /> },
          { path: 'stay/reservation', element: <StayReservationPage /> },
          { path: 'stay/consent-history', element: <StayConsentHistoryPage /> },
          // 제주 전용이던 구 경로. 북마크·최근 탭 호환용으로 지점 중립 경로로 넘긴다.
          { path: 'stay/manage-jeju', loader: createPathRedirectLoader('/stay/reservation') },
          {
            path: 'stay/consent-history-jeju',
            loader: createPathRedirectLoader('/stay/consent-history'),
          },

          // 지점 메뉴 (mfms branch-region·branch-init 이식 + 관리 메뉴에서 옮긴 지점 화면)
          { path: 'branch-region/manage', element: <BranchRegionManagePage /> },
          { path: 'branch-region/mapping', element: <BranchRegionMappingPage /> },
          { path: 'branch-region/lifestyle-tag', element: <BranchRegionLifestyleTagPage /> },
          { path: 'branch-region/card-attribute', element: <BranchRegionCardAttributePage /> },
          { path: 'branch-region/sales-info', element: <BranchRegionSalesInfoPage /> },
          { path: 'branch-region/branch-init', element: <BranchInitPage /> },
          {
            path: 'branch-region/notification-branch-settings',
            element: <NotificationBranchSettingsPage />,
          },
          { path: 'branch-region/branches', element: <BranchesPage /> },
          // 메뉴 구조 변경 전 경로
          { path: 'branch-init', loader: createPathRedirectLoader('/branch-region/branch-init') },
          {
            path: 'setting/notification-branch-settings',
            loader: createPathRedirectLoader('/branch-region/notification-branch-settings'),
          },
          { path: 'setting/branches', loader: createPathRedirectLoader('/branch-region/branches') },

          // 관리비 메뉴 (mfms billing 이식 — 중첩 탭 대신 독립 라우트)
          { path: 'billing/regular', element: <BillingRegularPage /> },
          { path: 'billing/move-out', element: <BillingMoveOutPage /> },
          {
            path: 'billing/general-management-fee',
            element: <BillingGeneralManagementFeePage />,
          },

          // 객실 메뉴
          { path: 'room/door-lock-manage', element: <RoomDoorLockManagePage /> },
          { path: 'room/room-status', loader: () => replace('/room/status') },
          { path: 'room/status', element: <RoomStatusPage /> },
          { path: 'room/cardkey-stay-v2', loader: () => replace('/room/cardkey-stay') },
          { path: 'room/cardkey-stay', element: <RoomCardkeyStayPage /> },

          // 시설 메뉴
          { path: 'facility/inquiry', element: <FacilityInquiryPage /> },
          { path: 'facility/schedule', element: <FacilitySchedulePage /> },
          { path: 'facility/space', element: <FacilitySpacePage /> },
          { path: 'facility/spaces-reservations', element: <FacilitySpaceReservationsPage /> },
          { path: 'facility/space-calendar', element: <FacilitySpaceCalendarPage /> },

          // 출입 그룹 메뉴
          { path: 'user-group/manage', element: <UserGroupManagePage /> },

          // 방문자 메뉴
          { path: 'visitor/manage', element: <VisitorManagePage /> },

          // 부가서비스 메뉴
          { path: 'addition/manage', element: <AdditionManagePage /> },

          // 공지사항 메뉴
          { path: 'notice/manage', element: <NoticeManagePage /> },

          // 관리 메뉴
          { path: 'setting/manual-authorizer', element: <ManualAuthorizerPage /> },
          { path: 'setting/terms', element: <TermsManagePage /> },
          { path: 'setting/terms/editor', element: <TermsEditorPage /> },
          { path: 'setting/short-url', element: <ShortUrlManagePage /> },

          // 기타 메뉴
          { path: 'popup/manage', element: <PopupManagePage /> },

          // 데일리 프라이싱 메뉴 (mfms daily-pricing 이식) — 탭 셸 없이 독립 3페이지
          { path: 'daily-pricing/metrics', element: <DailyPricingMetricsPage /> },
          { path: 'daily-pricing/summary', element: <DailyPricingSummaryPage /> },
          { path: 'daily-pricing/settings', element: <DailyPricingSettingsPage /> },
        ],
      },
    ],
  },
];
